import React, { useContext, useEffect } from 'react';
import { Box, List, SxProps, Typography } from '@mui/material';
import { CgList } from 'react-icons/cg';
import {
	TbBuilding,
	TbBuildingCommunity,
	TbSettings,
	TbStack2,
	TbTicket,
	TbUsers,
} from 'react-icons/tb';
import {
	useGlobalDispatch,
	useGlobalState,
} from 'src/stateProviders/GlobalStateProvider';
import { SidebarItem } from './SidebarItem';
import { OrganizationSelector } from '../OrganizationSelector';
import { Button } from '@mui/material';
import { TbLogout } from 'react-icons/tb';
import { LogoutConfirmModal } from 'src/globalComponents/modal/modalContent/LogoutConfirmModal';
import { matchRoute } from 'src/utils/matchRoute';
import { auth, ConfigContext, http, parseJwt } from 'src/base-ui/lib';
import { useSidebarToggle } from '../../hooks/useSidebarToggle';
export const SIDEBAR_WIDTH = 300;
export const COLLAPSED_SIDEBAR_WIDTH = 70;
const ICON_SIZE = 24;

interface State {
	tenantId: string;
	role: string;
	tenant: null | any;
}

export function Sidebar({ customSx = {} }: { customSx?: SxProps }) {
	const globalState = useGlobalState();
	const globalDispatch = useGlobalDispatch();
	const { isCollapsed } = useSidebarToggle();
	const [logoutModalOpen, setLogoutModalOpen] = React.useState(false);

	const { config }: any = useContext(ConfigContext);
	const [state, setState] = React.useState<State>({
		tenantId: parseJwt(auth.getBearerToken())?.tenantId,
		role: parseJwt(auth.getBearerToken())?.role,
		tenant: null,
	});

	useEffect(() => {
		(async () => {
			const currentWorkspace = await http.get(
				{},
				`${config.uri.base}/tenant/${state.tenantId}`
			);

			setState({
				...state,
				tenant: currentWorkspace,
			});
		})();
	}, []);

	const sidebarItems = React.useMemo(() => {
		return [
			{
				href: '/quotations',
				title: 'Quotes',
				icon: <TbTicket size={ICON_SIZE} />,
			},
			{
				href: '/users',
				title: 'User Management',
				icon: <TbUsers size={ICON_SIZE} />,
			},
			{
				href: '/companies',
				title: 'Companies',
				icon: <TbBuildingCommunity size={ICON_SIZE} />,
			},
			{
				href: '/assembly',
				title: 'Assemblies',
				icon: <CgList size={ICON_SIZE} />,
			},
			{
				href: '/materials',
				title: 'Materials',
				icon: <TbStack2 size={ICON_SIZE} />,
			},
			{
				href: `/company/${globalState.company?._id}`,
				title: 'My Company',
				icon: <TbBuilding size={ICON_SIZE} />,
			},
			{
				href: '/settings',
				title: 'User Settings',
				icon: <TbSettings size={ICON_SIZE} />,
			},
		];
	}, [globalState.company?._id]);

	const currentWidth = isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : SIDEBAR_WIDTH;

	return (
		<Box
			component="nav"
			data-tour="sidebar"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				maxHeight: '100vh',
				width: currentWidth,
				transition: 'width 0.3s ease-in-out',
				...customSx,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					position: 'fixed',
					width: currentWidth,
					height: 'calc(100% - var(--app-bar-height))',
					overflowY: 'auto',
					overflowX: 'hidden',
					transition: 'width 0.3s ease-in-out',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						flex: 1,
					}}
				>
					<List
						sx={{
							pt: 0.5,
							pb: 0,
						}}
					>
						{sidebarItems.map((item) => {
							if (
								globalState.appUser?.role?.allowedRoutes &&
								matchRoute(
									item.href,
									globalState.appUser?.role?.allowedRoutes
								) == false
							) {
								return null;
							}
							return (
								<SidebarItem
									key={item.href}
									href={item.href}
									title={item.title}
									icon={item.icon}
									isCollapsed={isCollapsed}
								/>
							);
						})}
					</List>
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						mx: isCollapsed ? 0.5 : 1.5,
						mt: 1.5,
						mb: 2,
						transition: 'margin 0.3s ease-in-out',
					}}
				>
					{state?.role === 'Super Admin' && !isCollapsed && (
						<OrganizationSelector />
					)}
					<Button
						variant="text"
						color="secondary"
						startIcon={!isCollapsed ? <TbLogout size={20} /> : null}
						onClick={() => setLogoutModalOpen(true)}
						sx={{
							px: isCollapsed ? 0.5 : 1.5,
							justifyContent: isCollapsed ? 'center' : 'flex-start',
							textTransform: 'none',
							fontWeight: 600,
							width: '100%',
							minWidth: isCollapsed ? 'auto' : 'initial',
							transition: 'all 0.3s ease-in-out',
						}}
					>
						{isCollapsed ? <TbLogout size={20} /> : 'Log Out'}
					</Button>
					<LogoutConfirmModal
						modalOpen={logoutModalOpen}
						setModalOpen={setLogoutModalOpen}
					/>
				</Box>
			</Box>
		</Box>
	);
}
